import React from 'react';
import {
  Shield,
  Check,
  Users,
  Zap,
  Droplets,
  Bath
} from 'lucide-react';
import SEO from '../components/SEO';
import SectionHeading from '../components/SectionHeading';
import Button from '../components/Button';
import FacilityGallery from '../components/FacilityGallery';
import CTASection from '../components/CTASection';

export default function Facility({ onOpenConsultation }) {
  const essentialInfrastructure = [
    {
      title: "Multidisciplinary Professional Care Team",
      description: "Round-the-clock medical and psychiatric oversight from psychiatrists, general physicians, medical officers, nursing staff, clinical psychologists, psychiatric MSWs, and attentive caretakers.",
      icon: Users
    },
    {
      title: "24/7 Electricity & Generator Backup",
      description: "Uninterrupted electrical supply backed by heavy-duty generator infrastructure, ensuring continuous illumination, climate comfort, and uninterrupted medical device operation.",
      icon: Zap
    },
    {
      title: "24/7 Hot & Cold Water Supply",
      description: "Geyser facilities and pressurized continuous hot and cold water supply across all patient residential wings and personal bathrooms.",
      icon: Droplets
    },
    {
      title: "Attached Indian & Western Washrooms",
      description: "Clean, hygienic, and regularly sanitized attached washrooms with both Indian and Western toilet options designed for maximum patient convenience and safety.",
      icon: Bath
    }
  ];

  return (
    <div className="bg-canvas">
      <SEO
        title="Facilities & Patient Amenities | UMED Psychiatric Hospital & Rehabilitation Centre"
        description="Explore UMED's supportive residential environment: private & general rooms, 24/7 medical care, nutritious meals, library, indoor & outdoor games, power backup, and pickup across Maharashtra."
      />

      {/* Hero */}
      <section className="relative pt-16 pb-20 lg:pt-24 lg:pb-28 bg-[#123B5D] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#0F766E]/30 via-[#123B5D] to-[#123B5D] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-[#0F766E] border border-[#2A9D8F]/30">
              <Shield className="w-3.5 h-3.5 text-[#2A9D8F]" />
              <span className="text-xs uppercase tracking-widest text-white font-semibold">
                Patient Amenities & Campus Infrastructure
              </span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-normal leading-tight">
              Comfortable Environment for <span className="text-[#2A9D8F]">Healing & Recovery</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#D9E8E5] leading-relaxed">
              UMED provides a supportive residential environment designed to promote comfort, safety, structured care and rehabilitation throughout the patient's journey.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Facility Gallery Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FacilityGallery onOpenConsultation={onOpenConsultation} />
        </div>
      </section>

      {/* Essential Infrastructure & Safety */}
      <section className="py-20 lg:py-28 bg-[#F5FAF9] border-t border-[#D9E8E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="SAFETY & COMFORT FIRST"
            title="Reliable Infrastructure & 24/7 Support"
            subtitle="Ensuring every patient experiences hygienic, continuous, and comfortable care throughout their stay."
            align="center"
            className="mb-16"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {essentialInfrastructure.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="p-8 rounded-2xl bg-white border border-[#D9E8E5] shadow-card flex items-start gap-5">
                  <div className="w-12 h-12 rounded-xl bg-[#F5FAF9] text-[#0F766E] flex items-center justify-center flex-shrink-0 border border-[#D9E8E5]">
                    <Icon className="w-6 h-6 text-[#0F766E]" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-[#123B5D] font-normal mb-2">
                      {item.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-[#64748B] leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Nutrition & Daily Routine */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <SectionHeading
                badge="NOURISHMENT & STRUCTURE"
                title="Healthy Meals & Structured Living Routine"
                subtitle="Regular meals and refreshments are provided as part of a wholesome daily schedule designed to restore physical vitality and circadian balance."
              />

              <div className="space-y-3 pt-2">
                {[
                  "Wholesome Breakfast to start the day with energy",
                  "Balanced and freshly prepared Lunch",
                  "Nutritious Dinner planned for restorative evening sleep",
                  "Tea and Coffee served twice a day during refreshment breaks",
                  "Hygienic food preparation under clean, monitored kitchen standards"
                ].map((point, i) => (
                  <div key={i} className="flex items-start gap-3 text-xs sm:text-sm text-[#64748B]">
                    <Check className="w-4 h-4 text-[#0F766E] flex-shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </div>
                ))}
              </div>

              <div className="pt-4">
                <Button onClick={onOpenConsultation} variant="primary" size="md">
                  Schedule a Campus Visit
                </Button>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-3xl overflow-hidden shadow-elevated border border-[#D9E8E5] aspect-[4/3] bg-[#123B5D]">
                <img
                  src="https://images.unsplash.com/photo-1498837167922-ddd27525d352?auto=format&fit=crop&w=1000&q=80"
                  alt="Healthy and nutritious food at UMED"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

          </div>
        </div>
      </section>



      {/* CTA */}
      <CTASection
        onOpenConsultation={onOpenConsultation}
        title="Looking for a Comfortable Recovery Environment?"
        subtitle="Contact the UMED team to explore our residential accommodation, treatment programs, and admission process."
        primaryButtonText="Inquire About Admission"
      />
    </div>
  );
}
